// react and js
import { React, useState, useRef, useCallback, forwardRef, useImperativeHandle, useEffect } from "react";
import PropTypes from "prop-types";

// External library
import JSZip from "jszip";

// mui
import { Stack, Box, IconButton, DialogTitle, DialogContent, DialogActions, Typography, CardContent, Chip, FormControl, Select } from "@mui/material";

// components
import ModalDialog from "../../../components/ModalDialog";
import RequestMessage from "../../../components/RequestMessage";
import Iconify from "../../../components/Iconify";
import SmartParameter from "../../../components/SmartParameter";

// theme
import {
  B1Tiny,
  ModalActionsLoadingButton,
  ModalContentMenuItem,
  ModalContentOutlinedInput,
  ModalAlertWarning,
  ModalAlertError,
} from "../../../theme/styled";

// utils
import { TxtDataToModuleData } from "../../../utils/moduleConversion";

// ----------------------------------------------------------------------
// Code
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const readFileContent = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsText(file);
  });

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const readZipFileContent = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      JSZip.loadAsync(e.target.result)
        .then((zip) => {
          const files = {};
          const promises = [];

          // Extract all .txt files from the ZIP
          zip.forEach((relativePath, zipEntry) => {
            if (relativePath.toLowerCase().endsWith(".txt") && !zipEntry.dir) {
              promises.push(
                zipEntry.async("text").then((content) => {
                  files[relativePath] = content;
                })
              );
            }
          });

          // Wait for all files to be extracted
          Promise.all(promises)
            .then(() => {
              if (Object.keys(files).length === 0) {
                reject(new Error("No .txt files found in ZIP archive"));
              } else {
                resolve(files);
              }
            })
            .catch((error) => {
              reject(new Error(`Failed to extract files from ZIP: ${error.message}`));
            });
        })
        .catch((error) => {
          reject(new Error(`Failed to read ZIP file: ${error.message}`));
        });
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsArrayBuffer(file);
  });

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const validateModulesAgainstExisting = (moduleDataArray, existingModules = [], duplicateAction = "skip") => {
  const validModules = [];
  const conflicts = [];
  const replacedModules = [];
  const createdNewModules = [];
  // Early return if no moduleDataArray
  if (!moduleDataArray || moduleDataArray.length === 0) {
    return { validModules: [], conflicts: [], replacedModules: [], createdNewModules: [] };
  }

  // Early return if no existing modules
  if (!existingModules || existingModules.length === 0) {
    return { validModules: moduleDataArray, conflicts: [], replacedModules: [], createdNewModules: [] };
  }

  // Extract existing module numbers and IDs from superDataStructure format
  const existingNumbers = existingModules.map((moduleData) => moduleData?.basics?.nNumber).filter((num) => num !== null && num !== undefined);

  const existingModuleIds = existingModules.map((moduleData) => moduleData?.moduleId).filter((id) => id !== null && id !== undefined);

  // Find the highest existing moduleId for creating new modules
  const maxExistingModuleId = existingModuleIds.length > 0 ? Math.max(...existingModuleIds) : 0;
  let nextAvailableModuleId = maxExistingModuleId + 1;

  // Track all numbers that will be used (existing + newly generated) to prevent duplicates
  const allUsedNumbers = [...existingNumbers];

  // Function to generate unique random module number (same algorithm as in ExpertAdvisorBuilder)
  const generateUniqueModuleNumber = () => {
    let moduleNumber = null;
    while (moduleNumber === null) {
      let isUnique = true;
      const moduleNumberTest = Math.floor(Math.random() * (9999 - 1000 + 1) + 1000);

      // Check against existing numbers AND numbers already generated in this batch
      if (allUsedNumbers.includes(moduleNumberTest)) {
        isUnique = false;
      }

      if (isUnique) {
        moduleNumber = moduleNumberTest;
        // Add to used numbers to prevent duplicates within this batch
        allUsedNumbers.push(moduleNumber);
      }
    }
    return moduleNumber;
  };

  moduleDataArray.forEach((moduleData, index) => {
    const moduleNumber = moduleData?.basics?.nNumber;

    if (moduleNumber && existingNumbers.includes(moduleNumber)) {
      if (duplicateAction === "skip") {
        conflicts.push({
          moduleNumber,
          moduleName: moduleData.moduleName || `Module ${moduleNumber}`,
          index,
        });
      } else if (duplicateAction === "create_new") {
        // Create new module with random unique number and ID
        const newRandomNumber = generateUniqueModuleNumber();
        const newModuleName = `Module ${newRandomNumber}`;
        const newModuleData = {
          ...moduleData,
          moduleId: nextAvailableModuleId,
          moduleName: newModuleName,
          basics: {
            ...moduleData.basics,
            nNumber: newRandomNumber,
          },
        };
        validModules.push(newModuleData);
        createdNewModules.push({
          originalNumber: moduleNumber,
          newNumber: newRandomNumber,
          moduleName: newModuleName,
        });
        nextAvailableModuleId += 1;
      } else if (duplicateAction === "replace") {
        // Replace existing module - find the existing module and create replacement
        const existingModule = existingModules.find((em) => em?.basics?.nNumber === moduleNumber);
        const existingModuleId = existingModule?.moduleId;

        // Create replacement module data with existing moduleId but imported content
        const replacementModuleData = {
          ...moduleData,
          moduleId: existingModuleId || moduleData.moduleId, // Use existing ID to maintain identity
          moduleName: moduleData.moduleName || `Module ${moduleNumber}`, // Keep imported module name
          _isReplacement: true, // Flag to indicate this is a replacement
          _replaceModuleId: existingModuleId, // ID of module to replace
        };

        // Add to validModules - parent needs to handle the _isReplacement flag
        validModules.push(replacementModuleData);
        replacedModules.push({
          moduleNumber,
          moduleName: moduleData.moduleName || `Module ${moduleNumber}`,
          index,
          existingModuleId,
        });
      }
    } else {
      // Module doesn't conflict with existing ones - add it normally
      validModules.push(moduleData);
      // Track this module's number to prevent future conflicts within this batch
      if (moduleNumber) {
        allUsedNumbers.push(moduleNumber);
      }
    }
  });

  return { validModules, conflicts, replacedModules, createdNewModules };
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const validateDuplicatesWithinBatch = (moduleDataArray) => {
  const validModules = [];
  const duplicates = [];
  const seenNumbers = new Set();

  moduleDataArray.forEach((moduleData, index) => {
    const moduleNumber = moduleData?.basics?.nNumber;

    if (moduleNumber && seenNumbers.has(moduleNumber)) {
      duplicates.push({
        moduleNumber,
        moduleName: moduleData.moduleName || `Module ${moduleNumber}`,
        index,
      });
    } else {
      if (moduleNumber) {
        seenNumbers.add(moduleNumber);
      }
      validModules.push(moduleData);
    }
  });

  return { validModules, duplicates };
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const deepCopyModuleData = (moduleData) => {
  return JSON.parse(JSON.stringify(moduleData));
};

const processFiles = async (files, existingModules = [], duplicateAction = "skip", maxModules = 200) => {
  const moduleDataArray = [];
  const errors = [];
  let batchIndex = 1;
  let skippedDueToLimit = 0;

  // Pre-generate all module numbers to ensure uniqueness
  const generateUniqueModuleNumbers = (count) => {
    const savedData = localStorage.getItem("expertAdvisorBuilderData");
    let modules = [];
    if (savedData) {
      modules = JSON.parse(savedData);
    }

    const existingNumbers = new Set();
    modules.forEach((module) => {
      const match = module.moduleName.match(/Module (\d+)/);
      const existingNumber = match ? parseInt(match[1], 10) : null;
      if (existingNumber) {
        existingNumbers.add(existingNumber);
      }
    });

    const generatedNumbers = [];
    while (generatedNumbers.length < count) {
      const moduleNumberTest = Math.floor(Math.random() * (9999 - 1000 + 1) + 1000);
      if (!existingNumbers.has(moduleNumberTest) && !generatedNumbers.includes(moduleNumberTest)) {
        generatedNumbers.push(moduleNumberTest);
      }
    }
    return generatedNumbers;
  };

  // Calculate how many modules we can add before hitting the limit
  const currentModuleCount = existingModules.length;
  const availableSlots = maxModules - currentModuleCount;

  // Count total files to process
  let totalFilesToProcess = 0;
  for (const file of files) {
    const fileExtension = `.${file.name.split(".").pop().toLowerCase()}`;
    if (fileExtension === ".txt") {
      totalFilesToProcess++;
    } else if (fileExtension === ".zip") {
      // For ZIP files, we need to count the .txt files inside
      try {
        const zipContent = await readZipFileContent(file);
        const zipEntries = Object.entries(zipContent);
        for (const [filename] of zipEntries) {
          if (filename.toLowerCase().endsWith(".txt")) {
            totalFilesToProcess++;
          }
        }
      } catch (error) {
        console.error(`Error counting files in ZIP ${file.name}:`, error);
      }
    }
  }

  // Check if we have enough slots
  if (availableSlots <= 0) {
    return {
      moduleDataArray: [],
      errors: [],
      conflicts: [],
      duplicates: [],
      replacedModules: [],
      createdNewModules: [],
      skippedDueToLimit: totalFilesToProcess,
      limitReached: true,
    };
  }

  // Limit the number of modules to process based on available slots
  const modulesToProcess = Math.min(totalFilesToProcess, availableSlots);
  skippedDueToLimit = Math.max(0, totalFilesToProcess - availableSlots);

  // Generate module numbers only for modules we can actually add
  const preGeneratedModuleNumbers = generateUniqueModuleNumbers(modulesToProcess);
  let moduleNumberIndex = 0;

  // Process each file completely before moving to the next
  for (const file of files) {
    const fileExtension = `.${file.name.split(".").pop().toLowerCase()}`;

    try {
      if (fileExtension === ".txt") {
        // Check if we've reached the limit
        if (moduleNumberIndex >= modulesToProcess) {
          break;
        }

        // Read and process the file
        const TxtData = await readFileContent(file);

        // Convert to module data with pre-generated module number
        const moduleNumber = preGeneratedModuleNumbers[moduleNumberIndex++];
        const moduleData = TxtDataToModuleData(TxtData, batchIndex, moduleNumber);

        // Deep copy and add to array
        const copiedModule = deepCopyModuleData(moduleData);
        moduleDataArray.push({
          ...copiedModule,
          sourceFile: file.name,
          batchIndex,
        });

        // Increment batch index for next file
        batchIndex++;

        // Optional: Add a small delay to ensure complete isolation
        await new Promise((resolve) => setTimeout(resolve, 10));
      } else if (fileExtension === ".zip") {
        const zipContent = await readZipFileContent(file);
        const zipEntries = Object.entries(zipContent);

        // Process each file in the ZIP sequentially
        for (const [filename, TxtData] of zipEntries) {
          if (filename.toLowerCase().endsWith(".txt")) {
            // Check if we've reached the limit
            if (moduleNumberIndex >= modulesToProcess) {
              break;
            }

            try {
              // Convert to module data with pre-generated module number
              const moduleNumber = preGeneratedModuleNumbers[moduleNumberIndex++];
              const moduleData = TxtDataToModuleData(TxtData, batchIndex, moduleNumber);

              // Deep copy and add to array
              const copiedModule = deepCopyModuleData(moduleData);
              moduleDataArray.push({
                ...copiedModule,
                sourceFile: `${file.name}/${filename}`,
                batchIndex,
              });

              // Increment batch index for next file
              batchIndex++;

              // Optional: Add a small delay between ZIP entries
              await new Promise((resolve) => setTimeout(resolve, 10));
            } catch (error) {
              console.warn(`Failed to parse ${filename} from ${file.name}:`, error);
              errors.push({
                file: `${file.name}/${filename}`,
                error: error.message,
              });
            }
          }
        }
      }
    } catch (error) {
      console.error(`Error processing ${file.name}:`, error);
      errors.push({
        file: file.name,
        error: error.message,
      });
    }
  }

  // First, check for duplicates within the import batch
  const { validModules: noDuplicatesModules, duplicates } = validateDuplicatesWithinBatch(moduleDataArray);

  // Then, validate against existing modules
  const { validModules, conflicts, replacedModules, createdNewModules } = validateModulesAgainstExisting(
    noDuplicatesModules,
    existingModules,
    duplicateAction
  );

  return {
    moduleDataArray: validModules,
    errors,
    conflicts,
    duplicates,
    replacedModules,
    createdNewModules,
    skippedDueToLimit,
  };
};

// ----------------------------------------------------------------------
// Main Component
// ----------------------------------------------------------------------
const HandleImportModule = forwardRef(
  (
    { onImport, pendingFiles = [], onClearPendingFiles, existingModules = [], duplicateAction = "skip", onDuplicateActionChange, maxModules = 200 },
    ref
  ) => {
    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    const [open, setOpen] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [, setDragCounter] = useState(0);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const fileInputRef = useRef(null);
    const wrapperRef = useRef(null);
    const [scroll, setScroll] = useState("paper");
    const [error, setError] = useState(false);
    const [success, setSuccess] = useState(false);
    const [warning, setWarning] = useState(false);
    const [returnMessage, setReturnMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const warningTimeoutRef = useRef(null);
    const autoCloseTimeoutRef = useRef(null);

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    const handleFileSelect = useCallback(
      (files) => {
        // Clear any existing warning timeout
        if (warningTimeoutRef.current) {
          clearTimeout(warningTimeoutRef.current);
          warningTimeoutRef.current = null;
        }

        // Reset all error states when selecting files
        setError(false);
        setSuccess(false);
        setWarning(false);
        setReturnMessage("");

        const allowedTypes = [".txt", ".zip"];
        const validFiles = [];
        const invalidFiles = [];

        files.forEach((file) => {
          const fileExtension = `.${file.name.split(".").pop().toLowerCase()}`;
          if (allowedTypes.includes(fileExtension)) {
            validFiles.push(file);
          } else {
            invalidFiles.push(file.name);
          }
        });

        if (invalidFiles.length > 0) {
          setWarning(true);
          setReturnMessage(`Invalid file types: ${invalidFiles.join(", ")}. Please select only .txt or .zip files.`);

          // Auto-clear warning after 5 seconds
          warningTimeoutRef.current = setTimeout(() => {
            setError(false);
            setSuccess(false);
            setWarning(false);
            setReturnMessage("");
            warningTimeoutRef.current = null;
          }, 5000);
          return;
        }

        const existingFileNames = selectedFiles.map((f) => f.name);
        const newFiles = validFiles.filter((file) => !existingFileNames.includes(file.name));
        const duplicateFiles = validFiles.filter((file) => existingFileNames.includes(file.name));

        if (duplicateFiles.length > 0) {
          setWarning(true);
          setReturnMessage(`Duplicate files detected: ${duplicateFiles.map((f) => f.name).join(", ")}. These files were skipped.`);

          // Auto-clear warning after 5 seconds
          warningTimeoutRef.current = setTimeout(() => {
            setError(false);
            setSuccess(false);
            setWarning(false);
            setReturnMessage("");
            warningTimeoutRef.current = null;
          }, 5000);

          if (newFiles.length > 0) {
            setSelectedFiles((prev) => [...prev, ...newFiles]);
          }
          return;
        }

        if (newFiles.length > 0) {
          setSelectedFiles((prev) => [...prev, ...newFiles]);
          // States are already reset at the beginning of this function
        }
      },
      [selectedFiles]
    );

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    useImperativeHandle(ref, () => ({
      openWithFiles: (files) => {
        if (files && files.length > 0) {
          setSelectedFiles([...files]);
        }
        setOpen(true);
        setError(false);
        setSuccess(false);
        setWarning(false);
        setReturnMessage("");
      },
    }));

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    useEffect(() => {
      if (pendingFiles && pendingFiles.length > 0 && !open) {
        setSelectedFiles([...pendingFiles]);
        setOpen(true);
        setError(false);
        setSuccess(false);
        setWarning(false);
        setReturnMessage("");
      }
    }, [pendingFiles, open]);

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    const handleClose = () => {
      // Clear any existing auto-close timeout
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
        autoCloseTimeoutRef.current = null;
      }

      // Clear any existing warning timeout
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
        warningTimeoutRef.current = null;
      }

      setOpen(false);
      setSelectedFiles([]);
      setError(false);
      setSuccess(false);
      setWarning(false);
      setReturnMessage("");
      setDragActive(false);
      setDragCounter(0);

      if (onClearPendingFiles) {
        onClearPendingFiles();
      }
    };

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    const handleDragEnter = useCallback(
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragCounter((prev) => prev + 1);
        if (!dragActive) {
          setDragActive(true);
        }
      },
      [dragActive]
    );

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    const handleDragLeave = useCallback((e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragCounter((prev) => {
        const newCount = prev - 1;
        if (newCount <= 0) {
          setDragActive(false);
          return 0;
        }
        return newCount;
      });
    }, []);

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    const handleDragOver = useCallback(
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!dragActive) {
          setDragActive(true);
        }
      },
      [dragActive]
    );

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    const handleDrop = useCallback(
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        setDragCounter(0);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
          const files = Array.from(e.dataTransfer.files);
          handleFileSelect(files);
        }
      },
      [handleFileSelect]
    );

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    const handleFileInputChange = (e) => {
      if (e.target.files && e.target.files.length > 0) {
        const files = Array.from(e.target.files);
        handleFileSelect(files);
      }
    };

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    const removeFile = (indexToRemove) => {
      // Clear any existing warning timeout when user interacts
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
        warningTimeoutRef.current = null;
      }

      setSelectedFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
      setError(false);
      setSuccess(false);
      setWarning(false);
      setReturnMessage("");
    };

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    const handleImport = async () => {
      // Clear any existing warning timeout and messages before starting import
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
        warningTimeoutRef.current = null;
      }

      // Clear all existing messages before starting
      setError(false);
      setWarning(false);
      setSuccess(false);
      setReturnMessage("");

      if (selectedFiles.length === 0) {
        setWarning(true);
        setReturnMessage("Please select at least one file to import");
        return;
      }

      setIsSubmitting(true);

      try {
        // Process all files and get structured data with validation
        const { moduleDataArray, errors, conflicts, duplicates, replacedModules, createdNewModules, skippedDueToLimit, limitReached } =
          await processFiles(selectedFiles, existingModules, duplicateAction, maxModules);

        if (errors.length > 0) {
          console.warn("Some files failed to import:", errors);
        }

        // Build comprehensive message about processed modules
        const actionMessages = [];

        // Handle duplicates within import batch
        if (duplicates.length > 0) {
          const duplicateNumbers = duplicates.map((d) => d.moduleNumber).join(", ");
          actionMessages.push(`${duplicates.length} duplicate(s) within import batch skipped (Module numbers: ${duplicateNumbers})`);
        }

        // Handle conflicts with existing modules based on action
        if (conflicts.length > 0 && duplicateAction === "skip") {
          const conflictNumbers = conflicts.map((c) => c.moduleNumber).join(", ");
          actionMessages.push(`${conflicts.length} existing module(s) skipped (Module numbers: ${conflictNumbers})`);
        }

        // Handle replaced modules
        if (replacedModules && replacedModules.length > 0) {
          const replacedNumbers = replacedModules.map((r) => r.moduleNumber).join(", ");
          actionMessages.push(`${replacedModules.length} existing module(s) replaced (Module numbers: ${replacedNumbers})`);
        }

        // Handle modules created with new numbers
        if (createdNewModules && createdNewModules.length > 0) {
          const createdInfo = createdNewModules.map((c) => `${c.originalNumber}→${c.newNumber}`).join(", ");
          actionMessages.push(`${createdNewModules.length} module(s) created with new numbers (${createdInfo})`);
        }

        // Handle modules skipped due to limit
        if (skippedDueToLimit > 0) {
          actionMessages.push(`${skippedDueToLimit} module(s) skipped due to reaching the ${maxModules} module limit`);
        }

        // Check if limit was reached before any import
        if (limitReached) {
          setError(true);
          setReturnMessage(`Cannot import modules: The maximum limit of ${maxModules} modules has already been reached.`);
          setIsSubmitting(false);
          return;
        }

        // Check if all modules were processed (none imported)
        const totalSkipped = duplicates.length + (duplicateAction === "skip" ? conflicts.length : 0) + skippedDueToLimit;
        if (moduleDataArray.length === 0 && totalSkipped > 0) {
          setError(true);
          setReturnMessage(`All modules were skipped: ${actionMessages.join(", ")}`);
          setIsSubmitting(false);
          return;
        }

        if (moduleDataArray.length > 0) {
          // Pass the validated data to the parent component
          await onImport(moduleDataArray);

          // Show final result message - only one message based on the outcome
          if (actionMessages.length > 0) {
            setSuccess(true);
            setReturnMessage(`${moduleDataArray.length} module(s) imported successfully! ${actionMessages.join(", ")}`);
          } else {
            setSuccess(true);
            setReturnMessage(`${moduleDataArray.length} module(s) processed successfully!`);
          }

          // autoCloseTimeoutRef.current = setTimeout(() => {
          //  handleClose();
          // }, 1500);
        } else {
          setError(true);
          setReturnMessage("No modules were successfully imported. Please check the file formats.");
        }
      } catch (err) {
        setError(true);
        setReturnMessage(err.message || "Failed to import modules. Please check the file formats.");
      } finally {
        setIsSubmitting(false);
      }
    };

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    const handleClickOpen = (scrollType) => () => {
      setOpen(true);
      setScroll(scrollType);
    };

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    const getIcon = () => {
      if (selectedFiles.length > 0) {
        return "eva:checkmark-circle-2-fill";
      }
      return "eva:plus-circle-outline";
    };

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    const getIconColor = () => {
      if (selectedFiles.length > 0) {
        return "#4caf50";
      }
      if (dragActive) {
        return "black";
      }
      return "#C8C8C8";
    };

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    const formatFileSize = (bytes) => {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
    };

    // ----------------------------------------------------------------------
    // Main component
    // ----------------------------------------------------------------------
    return (
      <>
        <B1Tiny fullWidth disableRipple={Boolean(true)} onClick={handleClickOpen("paper")}>
          Import module
        </B1Tiny>

        <ModalDialog
          open={open}
          handleClose={handleClose}
          scroll={scroll}
          maxWidth="sm"
          fullWidth={false}
          PaperProps={{
            sx: {
              width: "90vw",
              maxWidth: "500px",
              margin: "auto",
            },
          }}
          outlet={
            <>
              <DialogTitle
                id="dialog-title"
                sx={{
                  background: "#F2F2F2",
                  textTransform: "none",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  alignContent: "center",
                  py: 1,
                  pl: 2,
                  pr: 1,
                }}
              >
                <Typography variant="contentBig">Import module</Typography>

                <IconButton onClick={handleClose} sx={{ borderRadius: "10px" }}>
                  <Iconify icon="eva:close-outline" minWidth={25} minHeight={25} color="#2E2E2E" />
                </IconButton>
              </DialogTitle>

              <DialogContent
                dividers
                sx={{
                  background: "#F2F2F2",
                  borderTop: "1px solid #C8C8C8",
                  borderBottom: "0px solid #C8C8C8",
                  px: 2,
                  pt: 2,
                  pb: 1,
                  width: "100%",
                  boxSizing: "border-box",
                  "&::-webkit-scrollbar": {
                    width: "16px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#e0e0e0",
                    borderRadius: "6px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#888888",
                    borderRadius: "6px",
                    border: "2px solid #e0e0e0",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#666666",
                  },
                  scrollbarWidth: "thin",
                  scrollbarColor: "#888888 #e0e0e0",
                }}
              >
                <Stack spacing={2}>
                  {existingModules.length >= maxModules && (
                    <Box sx={{ m: -1 }}>
                      <ModalAlertError severity="error">
                        Maximum module limit reached ({existingModules.length}/{maxModules}). You cannot import any more modules until you remove some
                        existing ones.
                      </ModalAlertError>
                    </Box>
                  )}

                  {existingModules.length < maxModules && existingModules.length > maxModules - 10 && (
                    <Box sx={{ m: -1 }}>
                      <ModalAlertWarning severity="error">
                        You have {existingModules.length} of {maxModules} modules. Only {maxModules - existingModules.length} more can be imported.
                      </ModalAlertWarning>
                    </Box>
                  )}

                  <Typography variant="contentMiddle">
                    Import trading modules from TXT files or ZIP archives. You can select multiple files at once by dragging and dropping them or
                    using the file browser.
                  </Typography>

                  <Typography variant="contentMiddle">
                    Did you know you don't need to open this dialogue to drag and drop your trading module? You can drag and drop files anywhere
                    within the user interface at any time to automatically open this dialogue!
                  </Typography>

                  {/* Duplicate Module Action Dropdown */}
                  <Box>
                    <SmartParameter value="Action when module already exists" helpReference="HandleImportModule" helpText="" fieldName="Action" />
                    <FormControl fullWidth>
                      <Select
                        value={duplicateAction}
                        input={<ModalContentOutlinedInput />}
                        onChange={(event) => {
                          const newValue = event.target.value;
                          onDuplicateActionChange(newValue);
                        }}
                      >
                        <ModalContentMenuItem value="skip">Skip if module already exists</ModalContentMenuItem>
                        <ModalContentMenuItem value="create_new">Create new module with different module number</ModalContentMenuItem>
                        <ModalContentMenuItem value="replace">Replace existing module</ModalContentMenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  <Box
                    sx={{
                      border: dragActive ? "2px dashed black" : "2px dashed #C8C8C8",
                      backgroundColor: dragActive ? "#f3f7ff" : "#ffffff",
                      borderRadius: "10px",
                      minHeight: selectedFiles.length > 0 ? "auto" : "150px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: selectedFiles.length > 0 ? "flex-start" : "center",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      p: 2,
                      width: "100%",
                      "&:hover": {
                        borderColor: "black",
                        backgroundColor: "#f8f9fa",
                      },
                    }}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {selectedFiles.length > 0 ? (
                      <Stack spacing={2} sx={{ width: "100%", pointerEvents: "none" }}>
                        {/* File cards */}
                        <Stack spacing={1}>
                          {selectedFiles.map((file, index) => (
                            <Box
                              key={index}
                              sx={{
                                backgroundColor: "#F2F2F2",
                                border: "1px solid #C8C8C8",
                                borderRadius: "8px",
                                position: "relative",
                                pointerEvents: "auto",
                              }}
                            >
                              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <Iconify
                                      icon={file.name.endsWith(".zip") ? "eva:archive-outline" : "eva:file-text-outline"}
                                      sx={{ fontSize: 20, color: "#black" }}
                                    />
                                    <Stack>
                                      <Typography
                                        variant="contentSmall"
                                        sx={{
                                          fontWeight: 600,
                                          wordBreak: "break-word",
                                          maxWidth: "200px",
                                        }}
                                      >
                                        {file.name}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {formatFileSize(file.size)}
                                      </Typography>
                                    </Stack>
                                  </Stack>

                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <Chip
                                      label={file.name.endsWith(".zip") ? "ZIP" : "TXT"}
                                      size="small"
                                      sx={{
                                        backgroundColor: "white",
                                        color: "#666666",
                                        fontSize: "0.7rem",
                                        height: "20px",
                                      }}
                                    />
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        removeFile(index);
                                      }}
                                      sx={{
                                        width: 24,
                                        height: 24,
                                        color: "#666",
                                        "&:hover": {
                                          backgroundColor: "#ffebee",
                                          color: "#d32f2f",
                                        },
                                      }}
                                    >
                                      <Iconify icon="eva:close-outline" sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Stack>
                                </Stack>
                              </CardContent>
                            </Box>
                          ))}
                        </Stack>

                        {/* Add more files section */}
                        <Box
                          sx={{
                            border: "1px dashed #C8C8C8",
                            borderRadius: "8px",
                            p: 2,
                            backgroundColor: "#fafafa",
                            pointerEvents: "none",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          <Iconify icon="eva:plus-circle-outline" sx={{ fontSize: 32, color: "#C8C8C8" }} />
                          <Typography variant="contentSmall" color="text.secondary">
                            Add more files
                          </Typography>
                        </Box>
                      </Stack>
                    ) : (
                      <>
                        <Iconify
                          icon={getIcon()}
                          sx={{
                            fontSize: 48,
                            color: getIconColor(),
                            mb: 2,
                            pointerEvents: "none",
                          }}
                        />

                        <Stack alignItems="center" spacing={1} sx={{ width: "100%", pointerEvents: "none" }}>
                          <Typography variant="contentMiddle" sx={{ textAlign: "center" }}>
                            {dragActive ? "Drop your files here" : "Drag & drop your files here"}
                          </Typography>
                          <Typography variant="contentSmall" color="text.secondary" sx={{ textAlign: "center" }}>
                            or click to browse files
                          </Typography>
                          <Typography variant="contentSmall" color="text.secondary" sx={{ textAlign: "center" }}>
                            Supported formats: .txt, .zip
                          </Typography>
                        </Stack>
                      </>
                    )}
                  </Box>

                  <input ref={fileInputRef} type="file" accept=".txt,.zip" multiple onChange={handleFileInputChange} style={{ display: "none" }} />
                </Stack>
              </DialogContent>

              <Box sx={{ px: 1, pt: 1, mb: -1, background: "#F2F2F2" }}>
                <RequestMessage wrapperRef={wrapperRef} error={error} warning={warning} success={success} returnMessage={returnMessage} />
              </Box>

              <DialogActions
                sx={{
                  background: "#F2F2F2",
                  p: 2,
                  pt: 0,
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: "column",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <Box sx={{ pt: 1, width: "100%", display: "flex", gap: 1 }}>
                  <ModalActionsLoadingButton
                    fullWidth
                    size="large"
                    variant="contained"
                    onClick={handleImport}
                    loading={isSubmitting}
                    disabled={selectedFiles.length === 0 || existingModules.length >= maxModules}
                    sx={{ mt: 1 }}
                  >
                    {(() => {
                      if (selectedFiles.length === 0) {
                        return "Import";
                      }

                      const hasZipFiles = selectedFiles.some((file) => file.name.toLowerCase().endsWith(".zip"));

                      if (hasZipFiles) {
                        return `Import ${selectedFiles.length} File${selectedFiles.length !== 1 ? "s" : ""}`;
                      }

                      return `Import ${selectedFiles.length} Module${selectedFiles.length !== 1 ? "s" : ""}`;
                    })()}
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton fullWidth size="large" type="submit" variant="contained" onClick={handleClose} sx={{ mt: 1 }}>
                    Close
                  </ModalActionsLoadingButton>
                </Box>
              </DialogActions>
            </>
          }
        />
      </>
    );
  }
);

// PropTypes defined after component
HandleImportModule.propTypes = {
  onImport: PropTypes.func.isRequired,
  pendingFiles: PropTypes.array,
  onClearPendingFiles: PropTypes.func,
  existingModules: PropTypes.array,
  duplicateAction: PropTypes.string,
  onDuplicateActionChange: PropTypes.func.isRequired,
  maxModules: PropTypes.number,
};

export default HandleImportModule;
